import './index.scss';

import { useRef, useState } from "react";

import { useCommunity } from "contexts/community";

import { CommunityBanner, UserAvatar } from "@components/Community";

import { Button, Text } from "@fragments";
import Popper from "@fragments/Popper";

import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import { ArrowDropDownRounded, LinkRounded, ShareRounded } from "@mui/icons-material";
import * as Material from "@mui/material";

import {
    IconBrandInstagram,
    IconBrandTiktok,
    IconBrandWhatsapp,
    IconBrandYoutube,
    IconHelpCircle,
    IconLink,
    IconMail,
    IconUsers,
    IconWorld,
} from "@tabler/icons-react";

const LINK_TYPE_ICON = {
    website: <IconWorld size={18} />,
    support: <IconHelpCircle size={18} />,
    whatsapp: <IconBrandWhatsapp size={18} />,
    instagram: <IconBrandInstagram size={18} />,
    tiktok: <IconBrandTiktok size={18} />,
    youtube: <IconBrandYoutube size={18} />,
    email: <IconMail size={18} />,
    default: <IconLink size={18} />,
};

const SOCIAL_TYPES = new Set(['whatsapp', 'instagram', 'tiktok', 'youtube']);

const VALID_LINK_TYPES = new Set(Object.keys(LINK_TYPE_ICON));

function iconForType(type) {
    return LINK_TYPE_ICON[type] || <LinkRounded fontSize="small" />;
}

/**
 * Legacy backend payloads had `title` and `type` swapped on community links.
 * If `type` isn't a valid enum value but `title` is, swap them so this view
 * partitions and labels links correctly.
 */
function healLink(link) {
    if (!link || typeof link !== 'object') return link;
    const typeIsValid = VALID_LINK_TYPES.has(link.type);
    const titleLooksLikeType = VALID_LINK_TYPES.has(link.title);
    if (!typeIsValid && titleLooksLikeType) {
        return { ...link, title: link.type, type: link.title };
    }
    return link;
}

function partitionLinks(links = []) {
    const support = [];
    const social = [];
    const other = [];
    for (const raw of links) {
        const link = healLink(raw);
        const type = link?.type || 'default';
        if (type === 'support') support.push(link);
        else if (SOCIAL_TYPES.has(type)) social.push(link);
        else other.push(link);
    }
    return { support, social, other };
}



export default function Hero({ info, handleOpenUsers, loading }) {
    const { community: communityManagement } = useCommunity();

    const [loadingState, setLoadingState] = useState(false);

    const [subscribed, setSubscribed] = useState(info ? ['creator', 'owner', 'member'].includes(info.role) : false);

    const moreLinksPopperRef = useRef();

    async function handleSubscribeClick() {
        setLoadingState(true);
        await communityManagement.subscribe(info.id, () => {
            setSubscribed(true);
        });
        setLoadingState(false);
    }

    async function handleUnsubscribeClick() {
        setLoadingState(true);
        await communityManagement.unsubscribe(info.id, () => {
            setSubscribed(false);
        });
        setLoadingState(false);
    }

    function getSafeLink(link) {
        if (link?.includes('http')) return link;
        return 'https://' + link;
    }


    return (
        <div className="community-general-info mb-3">
            <div className="description">
                <CommunityBanner className="banner" size={128} banner={info?.banner} />

                <div className="pt-3" style={{ width: '100%' }}>
                    <div className="members-bar-content-inner">
                        <div className="community-title-container">
                            <div className="community-title">
                                <Text.h1 weight={500}>
                                    {info?.name}
                                </Text.h1>
                                {
                                    subscribed && info.role !== 'creator' && <Button
                                        tooltip="Gerenciar inscrição"
                                        color="inverse"
                                        size="small"
                                        loading={loadingState}
                                        endIcon={<ArrowDropDownRounded />}
                                        onClick={handleUnsubscribeClick}
                                    >
                                        Inscrito
                                    </Button>
                                }
                            </div>

                            {
                                !subscribed && info.role !== 'creator' && <Button
                                    tooltip="Se inscrever"
                                    color="inverse"
                                    size="small"
                                    loading={loadingState}
                                    endIcon={<ArrowDropDownRounded />}
                                    onClick={handleSubscribeClick}
                                >
                                    Se inscrever
                                </Button>
                            }

                            <div className="actions-row">
                                {
                                    info.isAdmin() && (
                                        <Button
                                            tooltip="Editar comunidade"
                                            color="overlay"
                                            onClick={() => {
                                                communityManagement.management.open(info);
                                            }}
                                            icon={<SettingsRoundedIcon />}
                                        />
                                    )
                                }
                                <Button
                                    tooltip="Compartilhar comunidade"
                                    onClick={() => {
                                        communityManagement.triggerShareModal(info.id);
                                    }}
                                    color="overlay"
                                    endIcon={<ShareRounded />}>
                                    Compartilhar
                                </Button>
                            </div>
                        </div>
                        <div className="communiy-description-info">
                            <div className="community-avatars">
                                {(info.members?.admins ?? []).slice(0, 3).map((admin, index) => (
                                    <div style={{ marginRight: '7px' }}>
                                        <UserAvatar size={24} user={admin} displayName={true} className="bsf-9 pr-1" style={{ padding: '4px', borderRadius: '50px' }} />
                                    </div>
                                ))}
                            </div>
                            {
                                (info.members?.admins ?? []).length > 3 && (
                                    <span style={{ opacity: 0.3, transform: 'translateY(1px)' }}>
                                        +{(info.members?.admins ?? []).length - 3} criadores •
                                    </span>
                                )
                            }
                            <span className='csf-5' style={{ transform: 'translateY(1px)' }} >
                                {window.helpers.pretty.int(info?.members?.total ?? 0)} membros
                            </span>

                            {info.isAdmin() && <Button
                                color="overlay"
                                variant="text"
                                size="small"
                                startIcon={<IconUsers size={18} />}
                                onClick={handleOpenUsers}
                            >
                                Gerenciar membros
                            </Button>}
                        </div>

                        <div className="mt-2 mb-2 text csf-2">
                            {
                                info?.description?.split("\n").map((line, key) => (
                                    <span className="description-line csf-4" key={key}>{line}</span>
                                ))
                            }

                            {
                                info?.links?.length > 0 && (() => {
                                    const { support, social, other } = partitionLinks(info.links);

                                    function openMoreLinks(e) {
                                        const options = other.slice(1).map((link, i) => ({
                                            id: `more-link-${i}`,
                                            label: link.title || link.url,
                                            icon: iconForType(link.type),
                                            onClick: () => window.open(getSafeLink(link.url), '_blank'),
                                        }));
                                        moreLinksPopperRef.current?.open(options, 'bottom-start', e.currentTarget);
                                    }

                                    const firstOther = other[0];
                                    const extraCount = other.length - 1;

                                    return (
                                        <div className="description-links mt-2" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                            {support.map((link, key) => (
                                                <Button
                                                    key={`support-${key}`}
                                                    color="inverse"
                                                    size="small"
                                                    startIcon={iconForType('support')}
                                                    onClick={() => window.open(getSafeLink(link.url), '_blank')}
                                                >
                                                    {link.title || 'Suporte'}
                                                </Button>
                                            ))}

                                            {social.length > 0 && (
                                                <div className="social-links-group" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    {social.map((link, key) => (
                                                        <Button
                                                            key={`social-${key}`}
                                                            color="overlay"
                                                            size="small"
                                                            icon={iconForType(link.type)}
                                                            tooltip={link.title || link.type}
                                                            onClick={() => window.open(getSafeLink(link.url), '_blank')}
                                                        />
                                                    ))}
                                                </div>
                                            )}

                                            {firstOther && (
                                                <Button
                                                    color="overlay"
                                                    size="small"
                                                    startIcon={iconForType(firstOther.type)}
                                                    onClick={() => window.open(getSafeLink(firstOther.url), '_blank')}
                                                >
                                                    {firstOther.title || firstOther.url}
                                                </Button>
                                            )}

                                            {extraCount > 0 && (
                                                <Button
                                                    color="overlay"
                                                    size="small"
                                                    onClick={openMoreLinks}
                                                >
                                                    {`e mais ${extraCount} link${extraCount > 1 ? 's' : ''}`}
                                                </Button>
                                            )}

                                            <Popper controlRef={moreLinksPopperRef} />
                                        </div>
                                    );
                                })()
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}